/*
 * TF Cart Drawer
 *
 * Talks to Shopify's Ajax Cart API and re-renders sections/tf-cart-drawer.liquid
 * through the Section Rendering API, so all markup lives in the Liquid file.
 *
 * Public API:
 *   window.TFCart.open() / close() / refresh()
 *   document.dispatchEvent(new CustomEvent('tf-cart:refresh'))  // after changing the cart yourself
 * Events fired on document: tf-cart:open, tf-cart:close, tf-cart:rendered
 */
(() => {
  if (window.TFCart) return;

  const nativeFetch = window.fetch.bind(window);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(?:\.js|\.json)?\/?$/;
  const ADD_FORM_ACTION = /\/cart\/add(?:\.js)?\/?$/;
  const QTY_DEBOUNCE = 350;
  const NOTE_DEBOUNCE = 500;
  const STORE = {
    timer: 'tfCart:timerEnd',
    giftDeclined: 'tfCart:giftDeclined',
  };

  const storage = {
    get(key) {
      try { return window.sessionStorage.getItem(key); } catch (_) { return null; }
    },
    set(key, value) {
      try { window.sessionStorage.setItem(key, value); } catch (_) { /* storage blocked */ }
    },
    remove(key) {
      try { window.sessionStorage.removeItem(key); } catch (_) { /* storage blocked */ }
    },
  };

  const getDrawer = () => document.querySelector('tf-cart-drawer');

  const parseJSON = (el, fallback) => {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent) || fallback; } catch (_) { return fallback; }
  };

  const escapeHtml = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // Formats cents like the theme's `money` filter, copying separators and decimals from a sample ("$1,234.56", "€1.234,56", "¥1,235").
  const moneyFormatter = (sample) => {
    const match = sample && sample.match(/\d[\d.,'\s\u00a0]*\d/);
    if (!match) return (cents) => (cents / 100).toFixed(2);
    const number = match[0];
    const decimals = number.replace(/\D/g, '') === '123456';
    const decimalSep = decimals ? number.charAt(number.length - 3) : '';
    const thousandsSep = /^1\D/.test(number) ? number.charAt(1) : '';
    return (cents) => {
      const [whole, fraction] = (decimals ? (cents / 100).toFixed(2) : String(Math.round(cents / 100))).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return sample.replace(number, decimals ? grouped + decimalSep + fraction : grouped);
    };
  };

  // Attribute lookups without CSS.escape (cart keys contain ":").
  const findByAttr = (scope, selector, attr, value) =>
    Array.from(scope.querySelectorAll(selector)).find((el) => el.getAttribute(attr) === value) || null;

  const pathOf = (input) => {
    try {
      const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input && input.url;
      return href ? new URL(href, window.location.href).pathname : '';
    } catch (_) {
      return '';
    }
  };

  async function cartFetch(path, body, sections) {
    const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    const sectionList = (sections || []).join(',');
    let payload;
    if (body instanceof FormData) {
      if (sectionList) {
        body.set('sections', sectionList);
        body.set('sections_url', window.location.pathname);
      }
      payload = body;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(sectionList ? { ...body, sections: sectionList, sections_url: window.location.pathname } : body);
    }
    const response = await nativeFetch(root + path, { method: 'POST', headers, body: payload, credentials: 'same-origin' });
    let data = {};
    try { data = await response.json(); } catch (_) { /* empty body */ }
    if (!response.ok) {
      const message = typeof data.description === 'string' && data.description
        ? data.description
        : typeof data.message === 'string' ? data.message : '';
      const error = new Error(message);
      error.data = data;
      throw error;
    }
    return data;
  }

  class TFCartDrawer extends HTMLElement {
    connectedCallback() {
      this.config = parseJSON(this.querySelector('[data-tf-config]'), {});
      this.panel = this.querySelector('[data-tf-panel]');
      this.live = this.querySelector('[data-tf-live]');
      this.state = {};
      this.queue = Promise.resolve();
      this.qtyTimers = new Map();
      this.recsCache = new Map();
      this.pending = { lineError: null, discountError: null, alert: null, keepDiscountInput: false };
      this.noteDirty = false;
      this.addonSyncing = false;
      this.addonStopped = false;

      if (!this.listening) {
        this.listening = true;
        this.addEventListener('click', (event) => this.onClick(event));
        this.addEventListener('change', (event) => this.onChange(event));
        this.addEventListener('input', (event) => this.onInput(event));
        this.addEventListener('submit', (event) => this.onSubmit(event));
        this.addEventListener('keydown', (event) => this.onKeydown(event));
      }

      this.localizeRewards();
      this.afterRender();
    }

    disconnectedCallback() {
      clearInterval(this.timerInterval);
      document.documentElement.classList.remove('tf-cart-locked');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.removeAttribute('inert');
      this.classList.add('is-open');
      this.updateUpsellNav();
      document.documentElement.classList.add('tf-cart-locked');
      window.requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
      this.emit('open');
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('tf-cart-locked');
      const opener = this.opener;
      this.opener = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
      this.emit('close');
    }

    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`tf-cart:${name}`, { detail: { ...detail, drawer: this } }));
    }

    announce(message) {
      if (!this.live) return;
      this.live.textContent = '';
      window.setTimeout(() => { this.live.textContent = message; }, 50);
    }

    errorText(error) {
      return (error && error.message) || this.config.genericError || 'Something went wrong. Please try again.';
    }

    sectionIds() {
      const extra = String(this.config.extraSections || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      return [this.config.sectionId, ...extra];
    }

    // Cart writes run one at a time so responses always render in order.
    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    }

    setLoading(on) {
      this.loadingCount = Math.max(0, (this.loadingCount || 0) + (on ? 1 : -1));
      const busy = this.loadingCount > 0;
      this.classList.toggle('is-loading', busy);
      this.panel.setAttribute('aria-busy', String(busy));
    }

    async request(path, body) {
      this.setLoading(true);
      try {
        const data = await cartFetch(path, body, this.sectionIds());
        if (data.sections && data.sections[this.config.sectionId]) this.renderSections(data.sections);
        else await this.fetchSections();
        notifyTheme(data);
        return data;
      } finally {
        this.setLoading(false);
      }
    }

    // Queue a cart write; on failure show the error and re-sync with the server.
    mutate(path, body) {
      if (path === 'cart/add.js') this.resetExpiredTimer();
      return this.enqueue(() => this.request(path, body)).catch((error) => {
        this.pending.alert = this.errorText(error);
        return this.refresh();
      });
    }

    async fetchSections() {
      const url = `${root}cart?sections=${encodeURIComponent(this.sectionIds().join(','))}`;
      // No "Accept: application/json" here: with it, Shopify answers GET /cart with cart.js data and ignores `sections`.
      const response = await nativeFetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(this.config.genericError);
      this.renderSections(await response.json());
    }

    refresh() {
      return this.enqueue(() => this.fetchSections()).catch(() => {});
    }

    renderSections(sections) {
      if (!sections) return;
      const html = sections[this.config.sectionId];
      if (html) this.swapRegions(html);

      this.sectionIds().slice(1).forEach((id) => {
        const markup = sections[id];
        if (!markup) return;
        const target = document.getElementById(id) || document.getElementById(`shopify-section-${id}`);
        if (!target) return;
        const doc = new DOMParser().parseFromString(markup, 'text/html');
        const source = doc.querySelector('.shopify-section') || doc.body;
        target.innerHTML = source.innerHTML;
      });

      this.afterRender();
    }

    swapRegions(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const active = document.activeElement;
      const hadFocus = active && this.contains(active);
      const focusKey = hadFocus ? active.getAttribute('data-tf-focus-key') : null;

      const body = this.querySelector('[data-tf-region="body"]');
      const scrollTop = body ? body.scrollTop : 0;
      const openState = {};
      this.querySelectorAll('details[data-tf-collapsible]').forEach((el) => {
        openState[el.dataset.tfCollapsible] = el.open;
      });
      const progress = {};
      this.querySelectorAll('[data-tf-progress]').forEach((el) => {
        progress[el.dataset.tfProgress] = el.style.width;
      });
      const note = this.querySelector('[data-tf-note]');
      const noteValue = this.noteDirty && note ? note.value : null;
      const discountInput = this.querySelector('[data-tf-discount-input]');
      const discountValue = this.pending.keepDiscountInput && discountInput ? discountInput.value : null;
      const rewardsDone = {};
      this.querySelectorAll('[data-tf-rewards]').forEach((el) => {
        rewardsDone[el.dataset.tfRewards] = parseInt(el.dataset.done, 10) || 0;
      });

      this.querySelectorAll('[data-tf-region]').forEach((current) => {
        const next = doc.querySelector(`[data-tf-region="${current.dataset.tfRegion}"]`);
        if (next) current.replaceWith(document.adoptNode(next));
      });

      this.localizeRewards();

      const newBody = this.querySelector('[data-tf-region="body"]');
      if (newBody) newBody.scrollTop = scrollTop;
      this.querySelectorAll('details[data-tf-collapsible]').forEach((el) => {
        if (el.dataset.tfCollapsible in openState) el.open = openState[el.dataset.tfCollapsible];
      });
      if (noteValue !== null) {
        const newNote = this.querySelector('[data-tf-note]');
        if (newNote) newNote.value = noteValue;
      }
      if (discountValue !== null) {
        const newInput = this.querySelector('[data-tf-discount-input]');
        if (newInput) newInput.value = discountValue;
      }
      this.pending.keepDiscountInput = false;

      // Older express-checkout markup needs Shopify to re-scan the page; newer markup is a web component.
      const expressButtons = window.Shopify && window.Shopify.StorefrontExpressButtons;
      if (this.querySelector('[data-tf-express]') && expressButtons && typeof expressButtons.initialize === 'function') {
        try { expressButtons.initialize(); } catch (_) { /* best effort */ }
      }

      // Animate progress bars from their previous width.
      this.querySelectorAll('[data-tf-progress]').forEach((el) => {
        const from = progress[el.dataset.tfProgress];
        const to = el.style.width;
        if (!from || from === to) return;
        el.style.width = from;
        el.getBoundingClientRect();
        window.requestAnimationFrame(() => { el.style.width = to; });
      });

      const unlocked = Array.from(this.querySelectorAll('[data-tf-rewards][data-celebrate]')).some((el) => {
        const before = rewardsDone[el.dataset.tfRewards];
        return before !== undefined && (parseInt(el.dataset.done, 10) || 0) > before;
      });
      if (unlocked && this.isOpen) this.celebrate();

      if (hadFocus) {
        const target = focusKey && findByAttr(this, '[data-tf-focus-key]', 'data-tf-focus-key', focusKey);
        if (target && !target.disabled) target.focus({ preventScroll: true });
        else this.panel.focus({ preventScroll: true });
      }
    }

    // Reward amounts are set in the store's currency. Shoppers paying in another currency get them converted
    // at Shopify's exchange rate, the one checkout uses for discount minimums. Mirrors tf-cart-rewards.liquid.
    localizeRewards() {
      const rate = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      if (!rate || rate === 1) return;
      this.querySelectorAll('[data-tf-rewards][data-tiers]').forEach((el) => {
        let tiers;
        try { tiers = JSON.parse(el.dataset.tiers); } catch (_) { return; }
        if (!tiers.length) return;
        const money = moneyFormatter(el.dataset.money);
        const total = parseInt(el.dataset.total, 10) || 0;
        const amounts = tiers.map((tier) => Math.round(tier.base * rate));
        let done = 0; let prev = 0; let next = 0; let nextIndex = -1;
        amounts.forEach((amount, i) => {
          if (total >= amount) { done += 1; prev = amount; } else if (!next) { next = amount; nextIndex = i; }
        });
        const count = amounts.length;
        let percent;
        if (!next) {
          percent = 100;
        } else if (count === 1) {
          percent = Math.floor((total * 100) / next);
        } else {
          const segStart = done > 0 ? Math.floor(((2 * done - 1) * 1000) / (2 * count)) : 0;
          const segEnd = Math.floor(((2 * done + 1) * 1000) / (2 * count));
          percent = Math.floor((Math.floor(((segEnd - segStart) * (total - prev)) / (next - prev)) + segStart) / 10);
        }
        percent = Math.max(0, Math.min(100, percent));

        el.dataset.done = String(done);
        el.classList.toggle('is-complete', !next);
        const text = el.querySelector('[data-tf-rewards-text]');
        if (text) {
          text.innerHTML = next
            ? escapeHtml(el.dataset.msgProgress || '')
              .replace('[amount]', `<strong>${escapeHtml(money(next - total))}</strong>`)
              .replace('[reward]', `<strong>${escapeHtml(tiers[nextIndex].label)}</strong>`)
            : escapeHtml(el.dataset.msgComplete || '');
        }
        const track = el.querySelector('[role="progressbar"]');
        if (track) track.setAttribute('aria-valuenow', String(percent));
        const fill = el.querySelector('[data-tf-progress]');
        if (fill) fill.style.width = `${percent}%`;
        el.querySelectorAll('.tf-rewards__marker').forEach((marker, i) => marker.classList.toggle('is-unlocked', total >= amounts[i]));
        el.querySelectorAll('.tf-rewards__tier').forEach((tier, i) => {
          tier.classList.toggle('is-unlocked', total >= amounts[i]);
          const label = tier.querySelector('[data-tf-rewards-amount]');
          if (label) label.textContent = money(amounts[i]).replace(/[.,]00(?!\d)/, '');
        });
      });
    }

    afterRender() {
      this.state = parseJSON(this.querySelector('[data-tf-state]'), {});
      this.updateCounts(this.state.itemCount || 0);
      this.showPending();
      this.setupTimer();
      this.loadRecommendations();
      this.updateUpsellNav();
      this.syncAddons();
      this.emit('rendered', { state: this.state });
    }

    updateCounts(count) {
      const selector = this.config.countSelector;
      if (!selector) return;
      try {
        document.querySelectorAll(selector).forEach((el) => {
          if (!this.contains(el)) el.textContent = String(count);
        });
      } catch (_) { /* invalid selector in settings */ }
    }

    showPending() {
      const { lineError, discountError, alert } = this.pending;
      if (lineError) {
        const line = findByAttr(this, '[data-tf-line]', 'data-key', lineError.key);
        const slot = line && line.querySelector('[data-tf-line-error]');
        if (slot) {
          slot.textContent = lineError.message;
          slot.hidden = false;
        } else {
          this.showAlert(lineError.message);
        }
      }
      if (discountError) {
        const slot = this.querySelector('[data-tf-discount-error]');
        if (slot) {
          slot.textContent = discountError;
          slot.hidden = false;
        } else {
          this.showAlert(discountError);
        }
      }
      if (alert) this.showAlert(alert);
      this.pending.lineError = null;
      this.pending.discountError = null;
      this.pending.alert = null;
    }

    showAlert(message) {
      const slot = this.querySelector('[data-tf-alert]');
      if (!slot) return;
      slot.textContent = message;
      slot.hidden = false;
    }

    /* ---------- events ---------- */

    onClick(event) {
      if (event.target.closest('[data-tf-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const button = event.target.closest('[data-tf-action]');
      if (!button || button.disabled) return;
      const { tfAction: action, key } = button.dataset;

      switch (action) {
        case 'plus':
        case 'minus':
          this.stepQuantity(key, action === 'plus' ? 1 : -1);
          break;
        case 'remove':
          if (button.hasAttribute('data-gift') && this.state.gift) {
            storage.set(STORE.giftDeclined, String(this.state.gift.variantId));
          }
          this.setQuantity(key, 0, true);
          break;
        case 'upsell-add':
          this.addUpsell(button);
          break;
        case 'upsell-prev':
        case 'upsell-next':
          this.scrollUpsell(button, action === 'upsell-next' ? 1 : -1);
          break;
        case 'gift-add':
          storage.remove(STORE.giftDeclined);
          this.addonStopped = false;
          button.disabled = true;
          this.syncAddons();
          break;
        case 'discount-remove':
          this.removeDiscount(button.dataset.code);
          break;
        default:
      }
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-tf-qty]')) {
        const min = parseInt(target.dataset.min, 10) || 1;
        const max = target.max ? parseInt(target.max, 10) : Infinity;
        let value = parseInt(target.value, 10);
        if (Number.isNaN(value) || value < 0) value = 0;
        if (value > 0 && value < min) value = min;
        if (value > max) value = max;
        target.value = value;
        this.setQuantity(target.dataset.key, value);
      } else if (target.matches('[data-tf-giftwrap]')) {
        if (target.checked) {
          this.mutate('cart/add.js', { items: [{ id: Number(target.dataset.variantId), quantity: 1 }] });
        } else if (target.dataset.key) {
          this.mutate('cart/change.js', { id: target.dataset.key, quantity: 0 });
        }
      } else if (target.matches('[data-tf-upsell-variant]')) {
        const card = target.closest('[data-tf-upsell-card]');
        const option = target.selectedOptions[0];
        const price = card && card.querySelector('[data-tf-upsell-price]');
        if (price && option && option.dataset.price) price.textContent = option.dataset.price;
      }
    }

    onInput(event) {
      const target = event.target;
      if (target.matches('[data-tf-note]')) {
        this.noteDirty = true;
        clearTimeout(this.noteTimer);
        const value = target.value;
        this.noteTimer = window.setTimeout(() => {
          this.enqueue(() => cartFetch('cart/update.js', { note: value }))
            .then(() => {
              const current = this.querySelector('[data-tf-note]');
              if (!current || current.value === value) this.noteDirty = false;
            })
            .catch(() => {});
        }, NOTE_DEBOUNCE);
      } else if (target.matches('[data-tf-discount-input]')) {
        const error = this.querySelector('[data-tf-discount-error]');
        if (error) error.hidden = true;
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (form.matches('[data-tf-discount-form]')) {
        event.preventDefault();
        const input = form.querySelector('[data-tf-discount-input]');
        const code = input ? input.value.trim() : '';
        if (code) this.applyDiscount(code, form.dataset.error);
        return;
      }
      if (form.matches('[data-tf-checkout-form]')) {
        const button = form.querySelector('[type="submit"]');
        if (button) button.classList.add('is-loading');
      }
    }

    onKeydown(event) {
      if (event.key !== 'Tab' || !this.isOpen) return;
      const focusable = Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, iframe, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- cart actions ---------- */

    stepQuantity(key, direction) {
      const input = findByAttr(this, '[data-tf-qty]', 'data-key', key);
      if (!input) return;
      const step = parseInt(input.step, 10) || 1;
      const min = parseInt(input.dataset.min, 10) || 1;
      const max = input.max ? parseInt(input.max, 10) : Infinity;
      let value = (parseInt(input.value, 10) || 0) + direction * step;
      if (value < min) value = direction < 0 ? 0 : min;
      if (value > max) value = max;
      input.value = value;
      this.setQuantity(key, value);
    }

    setQuantity(key, quantity, immediate = false) {
      clearTimeout(this.qtyTimers.get(key));
      const line = findByAttr(this, '[data-tf-line]', 'data-key', key);
      if (line) line.classList.add('is-updating');

      const run = () => {
        this.qtyTimers.delete(key);
        this.enqueue(() => this.request('cart/change.js', { id: key, quantity }))
          .then(() => this.announce(quantity === 0 ? 'Item removed from cart' : 'Cart updated'))
          .catch((error) => {
            this.pending.lineError = { key, message: this.errorText(error) };
            return this.refresh();
          });
      };

      if (immediate) run();
      else this.qtyTimers.set(key, window.setTimeout(run, QTY_DEBOUNCE));
    }

    addUpsell(button) {
      const card = button.closest('[data-tf-upsell-card]');
      const select = card && card.querySelector('[data-tf-upsell-variant]');
      const id = Number(select ? select.value : button.dataset.variantId);
      if (!id) return;
      button.disabled = true;
      button.classList.add('is-loading');
      this.mutate('cart/add.js', { items: [{ id, quantity: 1 }] }).then(() => this.announce('Added to cart'));
    }

    addFromForm(form, submitter) {
      const button = submitter || form.querySelector('[type="submit"]');
      if (button && button.getAttribute('aria-disabled') === 'true') return Promise.resolve();
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('tf-adding');
      }
      clearFormError(form);
      this.resetExpiredTimer();

      return this.enqueue(() => this.request('cart/add.js', new FormData(form)))
        .then(() => {
          this.announce('Added to cart');
          // Dawn's quick-add pop-up normally closes itself after adding; do the same.
          const modal = form.closest('quick-add-modal, modal-dialog');
          if (modal && typeof modal.hide === 'function') modal.hide(true);
          if (this.config.openOnAdd) this.open(modal ? null : button);
        })
        .catch((error) => showFormError(form, button, this.errorText(error)))
        .finally(() => {
          if (!button) return;
          button.removeAttribute('aria-disabled');
          button.classList.remove('tf-adding');
        });
    }

    applyDiscount(code, invalidMessage) {
      const lower = code.toLowerCase();
      const existing = (this.state.discountCodes || []).filter((c) => c.toLowerCase() !== lower);

      return this.enqueue(async () => {
        this.setLoading(true);
        try {
          const data = await cartFetch('cart/update.js', { discount: [...existing, code].join(',') }, this.sectionIds());
          const codes = data.discount_codes;
          const match = Array.isArray(codes) ? codes.find((d) => String(d.code).toLowerCase() === lower) : null;
          if (Array.isArray(codes) && (!match || !match.applicable)) {
            // Shopify keeps unusable codes on the cart; take it back off.
            this.pending.discountError = invalidMessage || this.config.genericError;
            this.pending.keepDiscountInput = true;
            const cleaned = await cartFetch('cart/update.js', { discount: existing.join(',') }, this.sectionIds());
            this.renderSections(cleaned.sections);
          } else {
            this.renderSections(data.sections);
            this.announce('Discount applied');
          }
        } catch (error) {
          this.pending.discountError = this.errorText(error);
          this.pending.keepDiscountInput = true;
          await this.fetchSections();
        } finally {
          this.setLoading(false);
        }
      });
    }

    removeDiscount(code) {
      const lower = String(code).toLowerCase();
      const remaining = (this.state.discountCodes || []).filter((c) => c.toLowerCase() !== lower);
      return this.mutate('cart/update.js', { discount: remaining.join(',') });
    }

    // Keep the free gift and gift wrap consistent with the rest of the cart.
    // If a change doesn't stick (e.g. the gift sold out), stop for this page view.
    syncAddons() {
      if (this.addonSyncing || this.addonStopped) return;
      const { gift, wrap } = this.state;
      let task = null;
      let worked = null;

      if (wrap && wrap.orphan && wrap.key) {
        task = ['cart/change.js', { id: wrap.key, quantity: 0 }];
        worked = (state) => !(state.wrap && state.wrap.orphan);
      } else if (gift && gift.variantId) {
        if (!gift.eligible) storage.remove(STORE.giftDeclined);
        const declined = storage.get(STORE.giftDeclined) === String(gift.variantId);
        if (gift.eligible && !gift.inCart && gift.available && !declined) {
          task = ['cart/add.js', { items: [{ id: gift.variantId, quantity: 1, properties: { _tf_gift: 'true' } }] }];
          worked = (state) => state.gift && state.gift.inCart;
        } else if (gift.inCart && !gift.eligible) {
          task = ['cart/change.js', { id: gift.key, quantity: 0 }];
          worked = (state) => state.gift && !state.gift.inCart;
        } else if (gift.inCart && gift.quantity > 1) {
          task = ['cart/change.js', { id: gift.key, quantity: 1 }];
          worked = (state) => state.gift && state.gift.quantity <= 1;
        }
      }
      if (!task) return;

      this.addonSyncing = true;
      this.enqueue(() => this.request(task[0], task[1]))
        .then(() => { if (!worked(this.state)) this.addonStopped = true; })
        .catch(() => { this.addonStopped = true; })
        .finally(() => {
          this.addonSyncing = false;
          this.syncAddons();
        });
    }

    /* ---------- recommendations ---------- */

    async fetchRecommendations(productId, intent) {
      const key = `${productId}:${intent}`;
      if (!this.recsCache.has(key)) {
        const params = new URLSearchParams({ product_id: productId, limit: '10', section_id: this.config.sectionId, intent });
        let html = '';
        try {
          const response = await nativeFetch(`${root}recommendations/products?${params}`);
          if (response.ok) {
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const source = doc.querySelector('[data-tf-recs-source]');
            html = source ? source.innerHTML : '';
          }
        } catch (_) { /* recommendations are optional */ }
        this.recsCache.set(key, html);
      }
      return this.recsCache.get(key);
    }

    // Hand-picked upsells first, then Shopify recommendations: "complementary_related" uses the products paired in
    // Search & Discovery, topped up with related ones.
    async loadRecommendations() {
      const box = this.querySelector('[data-tf-recs]');
      if (!box) return;
      const productId = box.dataset.productId;
      const limit = parseInt(box.dataset.limit, 10) || 4;
      const setting = box.dataset.intent || 'related';
      const intents = setting === 'complementary_related' ? ['complementary', 'related'] : [setting];

      // Hand-picked upsells of the newest item (rendered by Liquid) stay first.
      const cards = [...box.querySelectorAll(':scope > [data-tf-picked]')].slice(0, limit);
      const seen = new Set(cards.map((card) => card.dataset.productId));
      for (const intent of intents) {
        if (cards.length >= limit) break;
        const html = await this.fetchRecommendations(productId, intent);
        // The drawer may have re-rendered while we were fetching; its own call fills the new list.
        if (this.querySelector('[data-tf-recs]') !== box) return;
        const inCart = new Set((this.state.productIds || []).map(String));
        const template = document.createElement('template');
        template.innerHTML = html;
        template.content.querySelectorAll('[data-tf-upsell-card]').forEach((card) => {
          const id = card.dataset.productId;
          if (cards.length < limit && !inCart.has(id) && !seen.has(id)) {
            seen.add(id);
            cards.push(card);
          }
        });
        if (cards.length >= limit) break;
      }

      if (this.querySelector('[data-tf-recs]') !== box) return;
      box.replaceChildren(...cards);
      const wrapper = box.closest('[data-tf-upsell]');
      if (wrapper) wrapper.hidden = cards.length === 0;
      this.updateUpsellNav();
    }

    // ‹ › arrows for the "You may also like" slider: shown only when it overflows.
    updateUpsellNav() {
      this.querySelectorAll('[data-tf-upsell]').forEach((wrapper) => {
        const nav = wrapper.querySelector('[data-tf-upsell-nav]');
        const list = wrapper.querySelector('.tf-upsell__list');
        if (!nav || !list) return;
        const sync = () => {
          const max = list.scrollWidth - list.clientWidth;
          const [prev, next] = nav.querySelectorAll('button');
          nav.hidden = max <= 2;
          if (prev) prev.disabled = list.scrollLeft <= 2;
          if (next) next.disabled = list.scrollLeft >= max - 2;
        };
        if (!list.dataset.tfNavBound) {
          list.dataset.tfNavBound = 'true';
          list.addEventListener('scroll', sync, { passive: true });
        }
        sync();
      });
    }

    scrollUpsell(button, direction) {
      const wrapper = button.closest('[data-tf-upsell]');
      const list = wrapper && wrapper.querySelector('.tf-upsell__list');
      if (!list) return;
      const card = list.querySelector('[data-tf-upsell-card]');
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const distance = card ? card.getBoundingClientRect().width + gap : list.clientWidth * 0.8;
      list.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    /* ---------- reward celebration ---------- */

    celebrate() {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const layer = this.querySelector('[data-tf-confetti]');
      if (!layer || typeof layer.animate !== 'function') return;
      const colors = this.config.confettiColors || ['#fb1993', '#121212', '#ffd23f'];
      const width = layer.clientWidth || 400;
      const height = layer.clientHeight || 700;
      layer.replaceChildren();

      for (let i = 0; i < 80; i += 1) {
        const piece = document.createElement('i');
        const size = 6 + Math.random() * 6;
        piece.style.width = `${size}px`;
        piece.style.height = `${size * 0.45}px`;
        piece.style.background = colors[i % colors.length];
        layer.appendChild(piece);

        // Fired from the lower left corner, up and to the right.
        const angle = (-80 + Math.random() * 55) * (Math.PI / 180);
        const power = 0.55 + Math.random() * 0.5;
        const dx = Math.cos(angle) * power * width * 1.2;
        const dy = Math.sin(angle) * power * height * 0.8;
        const spin = (Math.random() - 0.5) * 900;
        piece.animate(
          [
            { transform: 'translate(0, 0) rotate(0deg)', opacity: 1 },
            { transform: `translate(${dx * 0.75}px, ${dy}px) rotate(${spin / 2}deg)`, opacity: 1, offset: 0.5 },
            { transform: `translate(${dx}px, ${dy * 0.2 + height * 0.3}px) rotate(${spin}deg)`, opacity: 0 },
          ],
          { duration: 1500 + Math.random() * 900, easing: 'cubic-bezier(0.15, 0.7, 0.35, 1)', fill: 'forwards' }
        );
      }
      clearTimeout(this.confettiTimer);
      this.confettiTimer = window.setTimeout(() => layer.replaceChildren(), 2600);
    }

    /* ---------- countdown ---------- */

    setupTimer() {
      clearInterval(this.timerInterval);
      if (!this.state.itemCount) {
        storage.remove(STORE.timer);
        return;
      }
      const el = this.querySelector('[data-tf-timer]');
      if (!el) return;

      let end = parseInt(storage.get(STORE.timer), 10);
      if (!end) {
        end = Date.now() + (parseFloat(el.dataset.minutes) || 10) * 60000;
        storage.set(STORE.timer, String(end));
      }
      const clock = el.querySelector('[data-tf-timer-clock]');
      const text = el.querySelector('[data-tf-timer-text]');

      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          clearInterval(this.timerInterval);
          el.classList.add('is-expired');
          if (el.dataset.expired) {
            if (text) text.textContent = el.dataset.expired;
          } else {
            el.hidden = true;
          }
          return;
        }
        const minutes = Math.floor(left / 60000);
        const seconds = Math.floor((left % 60000) / 1000);
        if (clock) clock.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
      };
      tick();
      this.timerInterval = window.setInterval(tick, 1000);
    }

    resetExpiredTimer() {
      const end = parseInt(storage.get(STORE.timer), 10);
      if (end && end < Date.now()) storage.remove(STORE.timer);
    }
  }

  /* ---------- theme integration ---------- */

  // Dawn-based themes listen for this to refresh "in cart" quantities on product pages,
  // the same message Dawn's own add-to-cart sends.
  function notifyTheme(cartData) {
    try {
      // eslint-disable-next-line no-undef
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        // eslint-disable-next-line no-undef
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tf-cart-drawer', cartData });
      }
    } catch (_) { /* theme without pub/sub */ }
  }

  /* ---------- product form errors ---------- */

  function clearFormError(form) {
    const existing = form.querySelector('.tf-form-error');
    if (existing) existing.remove();
  }

  function showFormError(form, button, message) {
    clearFormError(form);
    const error = document.createElement('p');
    error.className = 'tf-form-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    if (button && form.contains(button)) button.insertAdjacentElement('afterend', error);
    else form.appendChild(error);
  }

  /* ---------- global hooks ---------- */

  const onCartPage = (drawer) => window.location.pathname === drawer.config.cartUrl;

  // Add-to-cart forms: capture phase, so the theme's own handler never runs.
  // On the cart page itself the theme keeps its normal behaviour.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const drawer = getDrawer();
      if (!drawer || !drawer.config.interceptForms || !(form instanceof HTMLFormElement)) return;
      if (onCartPage(drawer) || form.closest('tf-cart-drawer') || form.hasAttribute('data-tf-skip')) return;
      if (!ADD_FORM_ACTION.test(pathOf(form.getAttribute('action') || ''))) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.addFromForm(form, event.submitter);
    },
    true
  );

  // Cart icon / cart links open the drawer instead of the cart page.
  document.addEventListener(
    'click',
    (event) => {
      const drawer = getDrawer();
      if (!drawer || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const selector = ['[data-tf-cart-open]', drawer.config.toggleSelector].filter(Boolean).join(',');
      let trigger = null;
      try { trigger = event.target.closest(selector); } catch (_) { trigger = event.target.closest('[data-tf-cart-open]'); }
      if (!trigger || drawer.contains(trigger) || onCartPage(drawer)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.open(trigger);
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const drawer = getDrawer();
    if (drawer && drawer.isOpen) drawer.close();
  });

  // Other apps and theme scripts that change the cart: refresh, and open on add.
  let externalTimer = null;
  let externalAdd = false;
  function onExternalCartChange(kind) {
    const drawer = getDrawer();
    if (!drawer || !drawer.config.syncApps) return;
    if (kind === 'add') externalAdd = true;
    clearTimeout(externalTimer);
    externalTimer = window.setTimeout(() => {
      const shouldOpen = externalAdd && drawer.config.openOnAdd && !onCartPage(drawer);
      externalAdd = false;
      drawer.refresh().then(() => { if (shouldOpen) drawer.open(); });
    }, 150);
  }

  window.fetch = function tfFetch(input, init) {
    const match = pathOf(input).match(CART_MUTATION);
    const result = nativeFetch(input, init);
    if (match) result.then((response) => { if (response.ok) onExternalCartChange(match[1]); }, () => {});
    return result;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function tfXhrOpen(method, url, ...rest) {
    const match = pathOf(url).match(CART_MUTATION);
    if (match) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onExternalCartChange(match[1]);
      });
    }
    return xhrOpen.call(this, method, url, ...rest);
  };

  document.addEventListener('tf-cart:refresh', () => { const d = getDrawer(); if (d) d.refresh(); });

  // Coming back from checkout with the browser's back button restores a stale page.
  window.addEventListener('pageshow', (event) => {
    const d = getDrawer();
    if (event.persisted && d) d.refresh();
  });

  // Theme editor: show the drawer while its settings are being edited.
  const isDrawerSection = (event) => {
    const drawer = getDrawer();
    return drawer && event.detail && event.detail.sectionId === drawer.config.sectionId ? drawer : null;
  };
  document.addEventListener('shopify:section:select', (event) => { const d = isDrawerSection(event); if (d) d.open(); });
  document.addEventListener('shopify:block:select', (event) => { const d = isDrawerSection(event); if (d) d.open(); });
  document.addEventListener('shopify:section:deselect', (event) => { const d = isDrawerSection(event); if (d) d.close(); });
  document.addEventListener('shopify:section:load', (event) => {
    const d = isDrawerSection(event);
    if (d && window.Shopify && window.Shopify.designMode) d.open();
  });

  window.TFCart = {
    open: () => { const d = getDrawer(); if (d) d.open(); },
    close: () => { const d = getDrawer(); if (d) d.close(); },
    refresh: () => { const d = getDrawer(); return d ? d.refresh() : Promise.resolve(); },
  };

  if (!customElements.get('tf-cart-drawer')) customElements.define('tf-cart-drawer', TFCartDrawer);
})();
